import { formatLocalDate } from './format-local-date';
import { localTimeHhMm } from './local-time';

/**
 * Renders an Aurora `type: dateTime` scalar for display as `DD-MM-YYYY HH:mm`
 * in the viewer's own timezone.
 *
 * The counterpart of `formatIsoDate`, and the split matters: a `date` is a
 * calendar day and is never converted, while a `dateTime` is an INSTANT and is
 * always shown in the local frame. Converting a `date` would move it to the
 * neighbouring day; NOT converting a `dateTime` would show a reader someone
 * else's clock.
 *
 * The server applies no timezone, so the value arrives as stored — either a UTC
 * ISO timestamp or the raw Postgres timestamptz text (`2026-08-01 14:23:45+00`).
 * Both carry an explicit offset, so both parse to the correct instant.
 *
 * An unparseable value is echoed back rather than blanked: a cell that silently
 * empties hides the problem, one that shows `not-a-date` reports it.
 *
 * @param iso The ISO scalar, or `null` / `undefined` / `''` when absent.
 * @returns The formatted local value, or `''` when the input is absent.
 *
 * @example
 * formatIsoDateTime('2026-08-01T14:23:45Z'); // → '01-08-2026 16:23' in UTC+2
 */
export function formatIsoDateTime(iso: string | null | undefined): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;

  return `${formatLocalDate(date)} ${localTimeHhMm(date)}`;
}
